import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { AssetPrice } from '@/services/api';
import type { AssetType } from '@/types/api';

export type PriceStatus = 'loading' | 'success' | 'error' | 'stale';

export interface Asset {
  id: string;
  symbol: string;
  name: string;
  type: AssetType;
  exchange?: string;
  price?: number;
  change24h?: number;
  changePercent24h?: number;
  lastUpdated?: string;
  sourceUrl?: string; // URL of the API request that provided this data
  priceStatus: PriceStatus;
  displayOrder: number;
  isActive: boolean;
  category?: string;
  tags?: string[];
  holdings?: number; // 持有数量
}

interface AssetState {
  assets: Asset[];
  addAsset: (asset: Asset) => void;
  removeAsset: (id: string) => void;
  removeAssets: (ids: string[]) => void;
  updateAssetPrice: (id: string, priceData: Partial<AssetPrice>) => void;
  updateAssetPriceStatus: (id: string, status: PriceStatus) => void;
  updateAssetCategory: (id: string, category: string) => void;
  updateAssetHoldings: (id: string, holdings: number) => void;
  reorderAssets: (newOrder: Asset[]) => void;
  importAssets: (assets: Asset[]) => void;
  exportAssets: () => Asset[];
}

export const useAssetStore = create<AssetState>()(
  persist(
    (set, get) => ({
      assets: [
        { 
          id: 'bitcoin', 
          symbol: 'BTC', 
          name: 'Bitcoin', 
          type: 'crypto',
          priceStatus: 'loading',
          displayOrder: 0,
          isActive: true
        },
        { 
          id: 'nvda', 
          symbol: 'NVDA', 
          name: 'NVIDIA Corporation', 
          type: 'stock',
          priceStatus: 'loading',
          displayOrder: 1,
          isActive: true
        },
        { 
          id: 'nasdaq', 
          symbol: 'IXIC', 
          name: 'NASDAQ Composite', 
          type: 'index',
          priceStatus: 'loading',
          displayOrder: 2,
          isActive: true
        },
        { 
          id: 'usd_cny', 
          symbol: 'USD/CNY', 
          name: 'USD/CNY', 
          type: 'forex',
          priceStatus: 'loading',
          displayOrder: 3,
          isActive: true
        },
      ],
      addAsset: (asset) =>
        set((state) => ({
          assets: [...state.assets, asset],
        })),
      removeAsset: (id) =>
        set((state) => ({
          assets: state.assets.filter((a) => a.id !== id),
        })),
      removeAssets: (ids) =>
        set((state) => ({
          assets: state.assets.filter((a) => !ids.includes(a.id)),
        })),
      updateAssetPrice: (id, priceData) =>
        set((state) => ({
          assets: state.assets.map((a) => {
            if (a.id === id) {
              // For forex assets, ensure name matches symbol if it contains Chinese characters
              let updatedName = a.name;
              if (a.type === 'forex' && /[\u4e00-\u9fa5]/.test(a.name)) {
                updatedName = a.symbol;
              }
              
              // Build update object
              const update: Partial<Asset> = {
                name: updatedName,
                priceStatus: 'success' as PriceStatus,
              };
              
              // Update price fields if they exist
              if (priceData.current_price !== undefined) {
                update.price = priceData.current_price;
              }
              if (priceData.price_change_24h !== undefined) {
                update.change24h = priceData.price_change_24h;
              }
              if (priceData.price_change_percentage_24h !== undefined) {
                update.changePercent24h = priceData.price_change_percentage_24h;
              }
              if (priceData.last_updated !== undefined) {
                update.lastUpdated = priceData.last_updated;
              }
              
              // Always update sourceUrl if source_url is provided (non-empty string only)
              if (priceData.source_url !== undefined && priceData.source_url) {
                update.sourceUrl = priceData.source_url;
              }
              
              return {
                ...a,
                ...update,
              };
            }
            return a;
          }),
        })),
      updateAssetPriceStatus: (id, status) =>
        set((state) => ({
          assets: state.assets.map((a) =>
            a.id === id ? { ...a, priceStatus: status } : a
          ),
        })),
      updateAssetCategory: (id, category) =>
        set((state) => ({
          assets: state.assets.map((a) =>
            a.id === id ? { ...a, category } : a
          ),
        })),
      updateAssetHoldings: (id, holdings) =>
        set((state) => ({
          assets: state.assets.map((a) =>
            a.id === id ? { ...a, holdings } : a
          ),
        })),
      reorderAssets: (newOrder) => set({ assets: newOrder }),
      importAssets: (importedAssets) =>
        set((state) => {
          const existingIds = new Set(state.assets.map(a => a.id));
          const newAssets = importedAssets.filter(a => !existingIds.has(a.id));
          return {
            assets: [...state.assets, ...newAssets],
          };
        }),
      exportAssets: () => get().assets,
    }),
    {
      name: 'asset-storage',
    }
  )
);

